import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import AppLayout from '../../components/AppLayout';
import St from './PlatformOverview.module.css';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';
axios.defaults.withCredentials = true;

// Utility functions
const formatCurrency = (val) => {
  if (!val) return '$0';
  if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
  return `$${val.toFixed(0)}`;
};

const formatNumber = (val) => {
  if (!val) return '0';
  if (val >= 1000) return (val / 1000).toFixed(1) + 'K';
  return val.toFixed(0);
};

// Revenue by Tier Chart Component
function RevenueChart({ data }) {
  const { t } = useTranslation();
  if (!data || !data.months || data.months.length === 0) {
    return <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>{t('screens.platformOverview.noRevenueData')}</div>;
  }

  const months = data.months;
  const enterprise = data.enterprise || [];
  const professional = data.professional || [];
  const starter = data.starter || [];

  const maxTotal = Math.max(...months.map((_, j) => (enterprise[j] || 0) + (professional[j] || 0) + (starter[j] || 0)), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 20 }}>
      {/* Chart area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* Y-axis labels */}
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#94a3b8', marginBottom: 8, paddingRight: 12 }}>
          <span>{formatCurrency(maxTotal)}</span>
          <span>{formatCurrency(maxTotal / 2)}</span>
          <span>$0</span>
        </div>

        {/* Bars container */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flex: 1 }}>
          {months.map((month, i) => {
            const e = enterprise[i] || 0;
            const p = professional[i] || 0;
            const s = starter[i] || 0;
            const total = e + p + s;
            const barHeight = (total / maxTotal) * 100; // percentage

            const ePercent = total > 0 ? (e / total) * 100 : 0;
            const pPercent = total > 0 ? (p / total) * 100 : 0;
            const sPercent = total > 0 ? (s / total) * 100 : 0;

            return (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 0 }}>
                {/* Stacked bar */}
                <div style={{ width: '100%', height: `${barHeight}%`, display: 'flex', flexDirection: 'column', borderRadius: '2px 2px 0 0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', minHeight: 4 }}>
                  {ePercent > 0 && <div style={{ flex: ePercent, backgroundColor: '#800020', borderRadius: ePercent === 100 ? '2px 2px 0 0' : '2px 0 0 0' }} />}
                  {pPercent > 0 && <div style={{ flex: pPercent, backgroundColor: '#C4A265' }} />}
                  {sPercent > 0 && <div style={{ flex: sPercent, backgroundColor: '#cbd5e1', borderRadius: sPercent === 100 ? '0 0 2px 2px' : pPercent === 0 && ePercent === 0 ? '2px 2px 0 0' : '0' }} />}
                </div>
                {/* Month label */}
                <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500, textAlign: 'center', wordBreak: 'break-word' }}>{month}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 24, justifyContent: 'center', flexShrink: 0, paddingTop: 8, borderTop: '1px solid #f1f5f9' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ width: 14, height: 14, backgroundColor: '#800020', borderRadius: '2px' }} />
          <span style={{ fontSize: '13px', color: '#475569', fontWeight: 500 }}>{t('screens.platformOverview.enterprise')}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ width: 14, height: 14, backgroundColor: '#C4A265', borderRadius: '2px' }} />
          <span style={{ fontSize: '13px', color: '#475569', fontWeight: 500 }}>{t('screens.platformOverview.professional')}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ width: 14, height: 14, backgroundColor: '#cbd5e1', borderRadius: '2px' }} />
          <span style={{ fontSize: '13px', color: '#475569', fontWeight: 500 }}>{t('screens.platformOverview.starter')}</span>
        </div>
      </div>
    </div>
  );
}

const PlatformOverview = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const userLogin = useSelector(state => state.userLogin);
  const user = userLogin?.userInfo?.data?.user;

  // Access control: admin only (SaaS-level dashboard)
  useEffect(() => {
    if (user && user.role !== 'admin') {
      history.push('/dashboard');
    }
  }, [user, history]);

  const [period, setPeriod] = useState('90d');
  const [activeTab, setActiveTab] = useState('cohort');
  const [loading, setLoading] = useState(true);

  // Data states
  const [metrics, setMetrics] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [pipeline, setPipeline] = useState(null);
  const [usage, setUsage] = useState(null);
  const [health, setHealth] = useState([]);
  const [aiUsage, setAiUsage] = useState(null);

  // Fetch all metrics
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // ai-usage endpoint accepts 7d|30d|90d|12m — coerce existing period
      // selector (which uses 'ytd') into our supported set.
      const aiPeriod = period === 'ytd' ? '12m' : period;
      const [m, r, c, p, u, h, ai] = await Promise.all([
        axios.get(`${BASE_URL}/orgs/metrics/summary?period=${period}`, { withCredentials: true }).catch(() => null),
        axios.get(`${BASE_URL}/orgs/metrics/revenue-by-tier?period=${period}`, { withCredentials: true }).catch(() => null),
        axios.get(`${BASE_URL}/orgs/metrics/top-customers?period=${period}`, { withCredentials: true }).catch(() => null),
        axios.get(`${BASE_URL}/orgs/metrics/pipeline-health`, { withCredentials: true }).catch(() => null),
        axios.get(`${BASE_URL}/orgs/metrics/usage-metrics?period=${period}`, { withCredentials: true }).catch(() => null),
        axios.get(`${BASE_URL}/orgs/metrics/customer-health?period=${period}`, { withCredentials: true }).catch(() => null),
        axios.get(`${BASE_URL}/ai-usage?period=${aiPeriod}`, { withCredentials: true }).catch(() => null),
      ]);

      if (m?.data?.data) setMetrics(m.data.data);
      if (r?.data?.data) setRevenue(r.data.data);
      if (c?.data?.data) setCustomers(Array.isArray(c.data.data) ? c.data.data : []);
      if (p?.data?.data) setPipeline(p.data.data);
      if (u?.data?.data) setUsage(u.data.data);
      if (h?.data?.data) setHealth(Array.isArray(h.data.data) ? h.data.data : []);
      if (ai?.data?.data) setAiUsage(ai.data.data);
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!user || user.role !== 'admin') {
    return (
      <AppLayout>
        <div style={{ padding: '40px' }}>
          <p>{t('screens.platformOverview.noAccess')}</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className={St.viewport}>
        {/* Header */}
        <div className={St.header}>
          <div className={St.headerTitle}>{t('screens.platformOverview.title')}</div>
          <div className={St.headerControls}>
            <div className={St.timeSelector}>
              <button className={`${St.timeBtn} ${period === '30d' ? St.active : ''}`} onClick={() => setPeriod('30d')}>30D</button>
              <button className={`${St.timeBtn} ${period === '90d' ? St.active : ''}`} onClick={() => setPeriod('90d')}>90D</button>
              <button className={`${St.timeBtn} ${period === '12m' ? St.active : ''}`} onClick={() => setPeriod('12m')}>12M</button>
              <button className={`${St.timeBtn} ${period === 'ytd' ? St.active : ''}`} onClick={() => setPeriod('ytd')}>YTD</button>
            </div>
            <button className={St.btnSubtle} onClick={fetchData}>{t('screens.platformOverview.refresh')}</button>
            <button className={St.btnSubtle}>{t('screens.platformOverview.export')}</button>
          </div>
        </div>

        {/* Content */}
        <div className={St.content}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>{t('screens.platformOverview.loadingMetrics')}</div>
          ) : (
            <>
              {/* Tier 1: KPIs */}
              <div className={St.tier1}>
                <div className={St.kpi}>
                  <div className={St.kpiLabel}>{t('screens.platformOverview.mrr')}</div>
                  <div className={St.kpiValue}>{formatCurrency(metrics?.mrr || 0)}</div>
                  <div className={St.kpiSub}>{t('screens.platformOverview.thisMonth')}</div>
                  <div className={`${St.kpiTrend} ${metrics?.mrrGrowth >= 0 ? '' : St.negative}`}>
                    {metrics?.mrrGrowth ? (metrics.mrrGrowth >= 0 ? '+' : '') + metrics.mrrGrowth.toFixed(1) + '%' : '0%'} {t('screens.platformOverview.vsLastMonth')}
                  </div>
                </div>

                <div className={St.kpi}>
                  <div className={St.kpiLabel}>{t('screens.platformOverview.arr')}</div>
                  <div className={St.kpiValue}>{formatCurrency(metrics?.arr || 0)}</div>
                  <div className={St.kpiSub}>{t('screens.platformOverview.runRate')}</div>
                  <div className={St.kpiTrend}>+24.3% YoY</div>
                </div>

                <div className={St.kpi}>
                  <div className={St.kpiLabel}>{t('screens.platformOverview.totalCustomers')}</div>
                  <div className={St.kpiValue}>{metrics?.customerCount || 0}</div>
                  <div className={St.kpiSub}>{t('screens.platformOverview.payingSubs')}</div>
                  <div className={St.kpiTrend}>+2 new this month</div>
                </div>

                <div className={St.kpi}>
                  <div className={St.kpiLabel}>{t('screens.platformOverview.totalUsers')}</div>
                  <div className={St.kpiValue}>{metrics?.totalUsers || 0}</div>
                  <div className={St.kpiSub}>{t('screens.platformOverview.acrossTenants')}</div>
                  <div className={St.kpiTrend}>+8.2% from last month</div>
                </div>

                <div className={St.kpi}>
                  <div className={St.kpiLabel}>{t('screens.platformOverview.monthlyGrowth')}</div>
                  <div className={St.kpiValue}>{metrics?.mrrGrowth ? metrics.mrrGrowth.toFixed(1) : 0}%</div>
                  <div className={St.kpiSub}>{t('screens.platformOverview.mrrMom')}</div>
                  <div className={St.kpiTrend}>{t('screens.platformOverview.trendingUp')}</div>
                </div>

                <div className={St.kpi}>
                  <div className={St.kpiLabel}>{t('screens.platformOverview.nrr')}</div>
                  <div className={St.kpiValue}>{metrics?.nrr || 0}%</div>
                  <div className={St.kpiSub}>{t('screens.platformOverview.expansionIncluded')}</div>
                  <div className={St.kpiTrend}>+2.1% from last quarter</div>
                </div>
              </div>

              {/* Tier 2: Charts & Tables */}
              <div className={St.tier2}>
                {/* Revenue by Tier Chart */}
                <div className={St.panel}>
                  <div className={St.panelTitle}>{t('screens.platformOverview.revenueByTier')}</div>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <RevenueChart data={revenue} />
                  </div>
                </div>

                {/* Top Customers Table */}
                <div className={St.panel}>
                  <div className={St.panelTitle}>{t('screens.platformOverview.topCustomersByArr')}</div>
                  <div className={St.customersTable}>
                    <table>
                      <thead>
                        <tr>
                          <th>{t('screens.platformOverview.colOrganization')}</th>
                          <th>{t('screens.platformOverview.colArr')}</th>
                          <th>{t('screens.platformOverview.colTier')}</th>
                          <th>{t('screens.platformOverview.colHealth')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {customers.length > 0 ? (
                          customers.slice(0, 6).map((c, i) => (
                            <tr key={i}>
                              <td>{c.name}</td>
                              <td>{formatCurrency(c.arr)}</td>
                              <td>{c.tier}</td>
                              <td>{c.health}</td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>{t('screens.platformOverview.noCustomerData')}</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pipeline & Health */}
                <div className={St.panel}>
                  <div className={St.panelTitle}>{t('screens.platformOverview.pipelineHealth')}</div>
                  <div className={St.metricsList}>
                    <div className={St.metricItem}>
                      <span className={St.metricItemLabel}>{t('screens.platformOverview.activePipeline')}</span>
                      <span className={St.metricItemValue}>{formatCurrency(pipeline?.activePipeline || 0)}</span>
                    </div>
                    <div className={St.metricItem}>
                      <span className={St.metricItemLabel}>{t('screens.platformOverview.dealsInProgress')}</span>
                      <span className={St.metricItemValue}>{pipeline?.dealsInProgress || 0}</span>
                    </div>
                    <div className={St.metricItem}>
                      <span className={St.metricItemLabel}>{t('screens.platformOverview.avgDealSize')}</span>
                      <span className={St.metricItemValue}>{formatCurrency(pipeline?.avgDealSize || 0)}</span>
                    </div>
                    <div className={St.metricItem}>
                      <span className={St.metricItemLabel}>{t('screens.platformOverview.platformUptime')}</span>
                      <span className={St.metricItemValue}>{pipeline?.uptime || 0}%</span>
                    </div>
                    <div className={St.metricItem}>
                      <span className={St.metricItemLabel}>{t('screens.platformOverview.apiResponse')}</span>
                      <span className={St.metricItemValue}>{pipeline?.apiResponseTime || 0}ms</span>
                    </div>
                    <div className={St.metricItem}>
                      <span className={St.metricItemLabel}>{t('screens.platformOverview.errorRate')}</span>
                      <span className={St.metricItemValue}>{pipeline?.errorRate || 0}%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tier 3: Usage Metrics */}
              <div className={St.tier3}>
                <div className={St.usageCard}>
                  <div className={St.usageLabel}>{t('screens.platformOverview.dau')}</div>
                  <div className={St.usageValue}>{usage?.dau || 0}</div>
                  <div className={St.usageSub}>{t('screens.platformOverview.dauPercent', { pct: usage?.dauPercentage || 0 })}</div>
                </div>

                <div className={St.usageCard}>
                  <div className={St.usageLabel}>{t('screens.platformOverview.docsProcessed')}</div>
                  <div className={St.usageValue}>{formatNumber(usage?.documentsProcessed || 0)}</div>
                  <div className={St.usageSub}>{t('screens.platformOverview.thisMonth')}</div>
                </div>

                <div className={St.usageCard}>
                  <div className={St.usageLabel}>{t('screens.platformOverview.claimsProcessed')}</div>
                  <div className={St.usageValue}>{formatNumber(usage?.claimsProcessed || 0)}</div>
                  <div className={St.usageSub}>$124M in value</div>
                </div>

                <div className={St.usageCard}>
                  <div className={St.usageLabel}>{t('screens.platformOverview.aiQueries')}</div>
                  <div className={St.usageValue}>{formatNumber(usage?.aiQueries || 0)}</div>
                  <div className={St.usageSub}>{t('screens.platformOverview.weeklyDelta')}</div>
                </div>

                <div className={St.usageCard}>
                  <div className={St.usageLabel}>{t('screens.platformOverview.messagesSent')}</div>
                  <div className={St.usageValue}>{formatNumber(usage?.messagesSent || 0)}</div>
                  <div className={St.usageSub}>{t('screens.platformOverview.viaChat')}</div>
                </div>

                <div className={St.usageCard}>
                  <div className={St.usageLabel}>{t('screens.platformOverview.videoCalls')}</div>
                  <div className={St.usageValue}>{usage?.videoCalls || 0}</div>
                  <div className={St.usageSub}>23.4 hours total</div>
                </div>

                <div className={St.usageCard}>
                  <div className={St.usageLabel}>{t('screens.platformOverview.activeApiKeys')}</div>
                  <div className={St.usageValue}>{usage?.apiKeys || 0}</div>
                  <div className={St.usageSub}>9 integrations</div>
                </div>
              </div>

              {/* Tabs */}
              <div className={St.tabs}>
                <button className={`${St.tabBtn} ${activeTab === 'aiusage' ? St.active : ''}`} onClick={() => setActiveTab('aiusage')}>{t('screens.platformOverview.tabAiUsage')}</button>
                <button className={`${St.tabBtn} ${activeTab === 'cohort' ? St.active : ''}`} onClick={() => setActiveTab('cohort')}>{t('screens.platformOverview.tabCohort')}</button>
                <button className={`${St.tabBtn} ${activeTab === 'churn' ? St.active : ''}`} onClick={() => setActiveTab('churn')}>{t('screens.platformOverview.tabChurn')}</button>
                <button className={`${St.tabBtn} ${activeTab === 'geographic' ? St.active : ''}`} onClick={() => setActiveTab('geographic')}>{t('screens.platformOverview.tabGeographic')}</button>
                <button className={`${St.tabBtn} ${activeTab === 'adoption' ? St.active : ''}`} onClick={() => setActiveTab('adoption')}>{t('screens.platformOverview.tabAdoption')}</button>
              </div>

              {/* Tab Content */}
              {activeTab === 'aiusage' && (
                <div className={St.tabContent}>
                  {/* Top-line numbers */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
                    <div className={St.usageCard}>
                      <div className={St.usageLabel}>Total Spend</div>
                      <div className={St.usageValue}>${(aiUsage?.total?.spendUsd || 0).toFixed(2)}</div>
                      <div className={St.usageSub}>Claude Haiku 4.5</div>
                    </div>
                    <div className={St.usageCard}>
                      <div className={St.usageLabel}>API Calls</div>
                      <div className={St.usageValue}>{formatNumber(aiUsage?.total?.calls || 0)}</div>
                      <div className={St.usageSub}>Across all tenants</div>
                    </div>
                    <div className={St.usageCard}>
                      <div className={St.usageLabel}>Input Tokens</div>
                      <div className={St.usageValue}>{formatNumber(aiUsage?.total?.inputTokens || 0)}</div>
                      <div className={St.usageSub}>Sent to Claude</div>
                    </div>
                    <div className={St.usageCard}>
                      <div className={St.usageLabel}>Output Tokens</div>
                      <div className={St.usageValue}>{formatNumber(aiUsage?.total?.outputTokens || 0)}</div>
                      <div className={St.usageSub}>Returned by Claude</div>
                    </div>
                    <div className={St.usageCard}>
                      <div className={St.usageLabel}>Success Rate</div>
                      <div className={St.usageValue}>{((aiUsage?.total?.successRate ?? 1) * 100).toFixed(1)}%</div>
                      <div className={St.usageSub}>Anthropic returned 200</div>
                    </div>
                    <div className={St.usageCard}>
                      <div className={St.usageLabel}>Avg Latency</div>
                      <div className={St.usageValue}>{(aiUsage?.total?.avgLatencyMs || 0)}ms</div>
                      <div className={St.usageSub}>Per call</div>
                    </div>
                    <div className={St.usageCard}>
                      <div className={St.usageLabel}>Cache Hit Rate</div>
                      <div className={St.usageValue}>{((aiUsage?.total?.cacheHitRate ?? 0) * 100).toFixed(1)}%</div>
                      <div className={St.usageSub}>{formatNumber(aiUsage?.total?.cacheHits || 0)} hits → $0 cost</div>
                    </div>
                  </div>

                  {/* Per-org + per-call breakdown */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}>Spend by Organization</div>
                      <table className={St.customersTable}>
                        <thead>
                          <tr>
                            <th>Organization</th>
                            <th style={{ textAlign: 'right' }}>Calls</th>
                            <th style={{ textAlign: 'right' }}>Spend</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(aiUsage?.byOrg || []).length > 0 ? (
                            aiUsage.byOrg.slice(0, 10).map(o => (
                              <tr key={o.orgId}>
                                <td>{o.orgName}</td>
                                <td style={{ textAlign: 'right' }}>{formatNumber(o.calls)}</td>
                                <td style={{ textAlign: 'right' }}>${o.spendUsd.toFixed(4)}</td>
                              </tr>
                            ))
                          ) : (
                            <tr><td colSpan="3" style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>No AI usage in this period</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 8 }}>Spend by Operation</div>
                      <div className={St.metricsList}>
                        <div className={St.metricItem}>
                          <span className={St.metricItemLabel}>Classify Document</span>
                          <span className={St.metricItemValue}>
                            {formatNumber(aiUsage?.byCall?.classifyDocument?.calls || 0)} calls · ${(aiUsage?.byCall?.classifyDocument?.spendUsd || 0).toFixed(4)}
                          </span>
                        </div>
                        <div className={St.metricItem}>
                          <span className={St.metricItemLabel}>Extract Fields</span>
                          <span className={St.metricItemValue}>
                            {formatNumber(aiUsage?.byCall?.extractFields?.calls || 0)} calls · ${(aiUsage?.byCall?.extractFields?.spendUsd || 0).toFixed(4)}
                          </span>
                        </div>
                        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #f1f5f9', fontSize: 12, color: '#94a3b8' }}>
                          PII gateway tokenizes all text before Claude sees it. No real PII reaches Anthropic.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'cohort' && (
                <div className={St.tabContent}>
                  <table className={St.cohortTable}>
                    <thead>
                      <tr>
                        <th>Cohort</th>
                        <th>M0</th>
                        <th>M1</th>
                        <th>M2</th>
                        <th>M3</th>
                        <th>M6</th>
                        <th>M9</th>
                        <th>M12</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td>JAN 2023</td><td className={St.cohortCell}>100%</td><td className={St.cohortCell}>95%</td><td className={St.cohortCell}>92%</td><td className={St.cohortCell}>91%</td><td className={St.cohortCell}>89%</td><td className={St.cohortCell}>87%</td><td className={St.cohortCell}>88%</td></tr>
                      <tr><td>APR 2023</td><td className={St.cohortCell}>100%</td><td className={St.cohortCell}>92%</td><td className={St.cohortCell}>88%</td><td className={St.cohortCell}>85%</td><td className={St.cohortCell}>83%</td><td className={St.cohortCell}>81%</td><td></td></tr>
                      <tr><td>JUL 2023</td><td className={St.cohortCell}>100%</td><td className={St.cohortCell}>88%</td><td className={St.cohortCell}>82%</td><td className={St.cohortCell}>78%</td><td className={St.cohortCell}>76%</td><td></td><td></td></tr>
                      <tr><td>OCT 2023</td><td className={St.cohortCell}>100%</td><td className={St.cohortCell}>85%</td><td className={St.cohortCell}>76%</td><td className={St.cohortCell}>72%</td><td></td><td></td><td></td></tr>
                      <tr><td>JAN 2024</td><td className={St.cohortCell}>100%</td><td className={St.cohortCell}>80%</td><td className={St.cohortCell}>71%</td><td></td><td></td><td></td><td></td></tr>
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === 'churn' && (
                <div className={St.tabContent}>
                  <table className={St.churnTable}>
                    <thead>
                      <tr>
                        <th>Organization</th>
                        <th>Risk Level</th>
                        <th>Days Inactive</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {health.length > 0 ? (
                        health.map((h, i) => (
                          <tr key={i}>
                            <td>{h.organization}</td>
                            <td><span className={`${St.churnRisk} ${St[`risk${h.churnRisk}`]}`}>{h.churnRisk}</span></td>
                            <td>{h.daysInactive}</td>
                            <td>{h.reason}</td>
                          </tr>
                        ))
                      ) : (
                        <tr><td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>No churn data</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === 'geographic' && (
                <div className={St.tabContent}>
                  <div className={St.geoGrid}>
                    <div className={St.geoCard}>
                      <div className={St.geoTitle}>Northeast</div>
                      <div className={St.geoMetric}><span>Customers</span><span>9</span></div>
                      <div className={St.geoMetric}><span>ARR</span><span>$142.8K</span></div>
                      <div className={St.geoMetric}><span>Churn</span><span style={{ color: '#10b981' }}>-1.2%</span></div>
                    </div>
                    <div className={St.geoCard}>
                      <div className={St.geoTitle}>Southeast</div>
                      <div className={St.geoMetric}><span>Customers</span><span>8</span></div>
                      <div className={St.geoMetric}><span>ARR</span><span>$104.2K</span></div>
                      <div className={St.geoMetric}><span>Churn</span><span style={{ color: '#ef4444' }}>-8.3%</span></div>
                    </div>
                    <div className={St.geoCard}>
                      <div className={St.geoTitle}>Midwest</div>
                      <div className={St.geoMetric}><span>Customers</span><span>7</span></div>
                      <div className={St.geoMetric}><span>ARR</span><span>$89.4K</span></div>
                      <div className={St.geoMetric}><span>Churn</span><span style={{ color: '#10b981' }}>-0.5%</span></div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'adoption' && (
                <div className={St.tabContent}>
                  <div className={St.adoptionGrid}>
                    {[
                      { name: 'Claims Management', adoption: 94 },
                      { name: 'Document Vault', adoption: 88 },
                      { name: 'Video Conferencing', adoption: 71 },
                      { name: 'AI Assistance', adoption: 52 },
                      { name: 'Integrations', adoption: 41 },
                      { name: 'Compliance Reports', adoption: 38 },
                    ].map((item, i) => (
                      <div className={St.adoptionItem} key={i}>
                        <div className={St.adoptionName}>{item.name}</div>
                        <div className={St.adoptionBar}>
                          <div className={St.adoptionBarFill} style={{ width: `${item.adoption}%` }}></div>
                        </div>
                        <div className={St.adoptionStat}>{item.adoption}% of customers enabled</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default PlatformOverview;
